import { FormEvent, useState } from 'react';
import { MouseEventHandler, useRef } from 'react';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { useTenantRegisterService } from 'TenantModule/hooks/useTenantRegisterService';

export interface RegisterServiceFields {
  tenant: string;
  integrationAgentName: Nullable<string>;
  apiKeySecret: string;
  useDefault: boolean;
  realmId: string;
  dbHost: string;
  dbName: string;
  dbPassword: string;
  dbUser: string;
  roHost: string;
  dbSecretName: string;
  serviceRegion: string;
  serviceIPAddress: string;
}

export const useTenantRegisterServiceForm = () => {
  const {
    selectedTenant,
    onSelectRealm,
    selectedRealm,
    onSelectTenantRealm,
    selectedGlobalRealm,
    selectedGlobalTenant,
  } = useTenantRealmDropdownsFlow();
  const { registerTenantService, isRegisteringServiceToTenant } = useTenantRegisterService();

  const registerServiceDefaultValues: RegisterServiceFields = {
    tenant: selectedGlobalTenant?.tenantName || '',
    integrationAgentName: null,
    apiKeySecret: '',
    useDefault: true,
    realmId: selectedGlobalRealm?.realm_id || '',
    dbHost: '',
    dbName: '',
    dbPassword: '',
    dbUser: '',
    roHost: '',
    dbSecretName: '',
    serviceRegion: '',
    serviceIPAddress: '',
  };

  const [registerTenantServiceParams, setRegisterServiceParams] = useState<RegisterServiceFields>(
    registerServiceDefaultValues,
  );

  const isUsingDefaultDb = registerTenantServiceParams.useDefault;

  const areSomeDbFieldsFilled = Boolean(
    registerTenantServiceParams.dbHost ||
    registerTenantServiceParams.dbName ||
    registerTenantServiceParams.dbPassword ||
    registerTenantServiceParams.dbUser ||
    registerTenantServiceParams.roHost,
  );

  const isSecretNameFieldRequired = !isUsingDefaultDb;

  const realmDropdownRef = useRef<HTMLDivElement>(null);

  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({
        target: e.currentTarget,
        tenantId: selectedTenant.id,
      });
    }
  };

  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
        onDone: ({ tenant, realm }) => {
          if (tenant && realm) {
            setRegisterServiceParams((prevParams) => ({
              ...prevParams,
              tenant: tenant.tenantName,
              realmId: realm.realm_id,
            }));
          }
        },
      });
    }
  };

  const onRegisterServiceFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type } = event.target;

    if (type === 'checkbox') {
      setRegisterServiceParams((prevParams) => ({
        ...prevParams,
        [name]: event.target.checked,
      }));
    } else {
      setRegisterServiceParams((prevParams) => ({
        ...prevParams,
        [name]: event.target.value,
      }));
    }
  };

  const onSubmitTenantRegisterServiceForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const selectedServiceName = registerTenantServiceParams.integrationAgentName;

    if (selectedServiceName) {
      registerTenantService({
        tenant: registerTenantServiceParams.tenant,
        ...(registerTenantServiceParams.serviceRegion && {
          serviceRegion: registerTenantServiceParams.serviceRegion,
        }),
        ...(registerTenantServiceParams.serviceIPAddress && {
          serviceIPAddress: registerTenantServiceParams.serviceIPAddress,
        }),
        service: {
          name: selectedServiceName,
          serviceName: selectedServiceName,
        },
        // Optional: galaxy-server generates a random key when the field is
        // omitted, so a blank input is left out rather than sent as "".
        ...(registerTenantServiceParams.apiKeySecret.trim() && {
          apiKeySecret: registerTenantServiceParams.apiKeySecret.trim(),
        }),
        databaseSecret: {
          useDefault: registerTenantServiceParams.useDefault,
          secretName: registerTenantServiceParams.dbSecretName,
          realm: registerTenantServiceParams.realmId,
          service: selectedServiceName,
          // The credential inputs are hidden while "use Default" is checked;
          // values typed before re-checking it must not ride along, because
          // galaxy-server rejects a partial credential set outright.
          ...(!isUsingDefaultDb &&
            areSomeDbFieldsFilled && {
              credentials: {
                dbHost: registerTenantServiceParams.dbHost,
                dbName: registerTenantServiceParams.dbName,
                dbPassword: registerTenantServiceParams.dbPassword,
                dbUser: registerTenantServiceParams.dbUser,
                roHost: registerTenantServiceParams.roHost,
              },
            }),
        },
      });
    }
  };

  return {
    onSubmitTenantRegisterServiceForm,
    onRegisterServiceFormChange,
    isRegisteringServiceToTenant,
    registerTenantServiceParams,
    onRealmSelectorClick,
    onTenantSelectorClick,
    selectedTenant,
    selectedRealm,
    realmDropdownRef,
    isSecretNameFieldRequired,
    isUsingDefaultDb,
    areSomeDbFieldsFilled,
  };
};
